/**
 * Shared callable from the Flutter monorepo: fills canonical identity fields on users/{uid}
 * (merge-only, idempotent). See docs/user-creation-cf-info.md.
 */
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "@/services/firebaseService";

export const IDENTITY_BASELINE_MAX_SOURCE_LEN = 80;

export const IdentityBaselineSource = {
  CREATE_ACCOUNT_EMAIL: "pro_create_account_email",
  CREATE_ACCOUNT_GOOGLE: "pro_create_account_google",
  WELCOME_INVITE_PASSWORD: "pro_welcome_invite_password",
  LOGIN_APPROVED_GOOGLE: "pro_login_approved_google",
  AUTH_SESSION: "pro_auth_session"
} as const;

export type EnsureUserIdentityBaselineDeps = {
  callFn?: (data: { source?: string }) => Promise<unknown>;
};

function normalizeSource(source: string): string {
  return String(source ?? "")
    .trim()
    .slice(0, IDENTITY_BASELINE_MAX_SOURCE_LEN);
}

/**
 * Call ensureUserIdentityBaseline (us-east4). Caller must be signed in (non-anonymous).
 * Pass injectable deps.callFn in unit tests.
 */
export async function ensureUserIdentityBaseline(
  params: { source: string },
  deps?: EnsureUserIdentityBaselineDeps
): Promise<void> {
  const source = normalizeSource(params.source);
  if (!source) {
    throw new Error("ensureUserIdentityBaseline: source is required");
  }

  const run =
    deps?.callFn ??
    (async (data: { source?: string }) => {
      const functions = getFunctions(app, "us-east4");
      const fn = httpsCallable(functions, "ensureUserIdentityBaseline");
      return fn(data);
    });

  await run({ source });
}
